/**
 * The 3D model shelf and the models standing on the table. The shelf in the studio shows a
 * miniature of each model; selecting one places a full-size copy on the table, shared with
 * the room. Anyone can grip a model to carry it (ownership passes to whoever holds it, the
 * others see a ghost outline and who has it), turn it with the thumbstick, scale it with the
 * wheel, explode it into parts from its label, and pin a part by selecting it.
 */
import * as THREE from 'three';
import {
  ROLE_STYLES,
  type ModelKind,
  type ModelPin,
  type SharedModel,
  MODEL_KINDS,
} from '@clinspace/shared';
import type { Net } from '../app/net.ts';
import { CanvasPanel, type Region } from '../dashboards/CanvasPanel.ts';
import { PALETTE, chip, fillRoundRect, font, text, wrapText } from '../dashboards/charts.ts';
import type { StudyView } from '../dashboards/StudyView.ts';
import type { Interactive, InteractiveSystem } from '../ui/Interactive.ts';
import type { InputManager, Pointer } from '../xr/InputManager.ts';
import { ROOM, STUDIO_FITTINGS } from '../world/StudyRoom.ts';
import { modelData } from './modelData.ts';
import {
  MODEL_HINTS,
  MODEL_TITLES,
  MODEL_USES,
  buildModel,
  mergedGeometry,
  partPosition,
  type ModelSpec,
} from './models.ts';

/**
 * The shelf unit against the studio's north wall (2026-10-07; it stood right of the door).
 * Built facing -z and turned by `yaw`, so its front faces south into the studio.
 */
export const SHELF = {
  x: STUDIO_FITTINGS.shelf.x,
  y: 0.95,
  z: STUDIO_FITTINGS.shelf.z,
  yaw: Math.PI,
  width: 1.6,
  miniScale: 0.38,
};
const STREAM_MS = 100;
const MIN_SCALE = 0.4;
const MAX_SCALE = 3;
const TABLE_TOP = 0.97;
const PLACE_RADIUS = 0.95;

interface Placed {
  model: SharedModel;
  spec: ModelSpec;
  group: THREE.Group;
  parts: THREE.Mesh[];
  label: CanvasPanel;
  ghost: THREE.LineSegments;
  pins: THREE.InstancedMesh;
  /**
   * An invisible box around the model that only the grip ray tests, so a thin molecule or a
   * flat map is as easy to grab as the torso. It lives outside the model group so hovering
   * and pinning parts still hit the parts.
   */
  pick: THREE.Mesh;
  interactive: Interactive;
  hoveredPart: number;
}

interface Carry {
  id: string;
  pointerId: string;
  offset: THREE.Matrix4;
}

export interface ModelShelfHandlers {
  tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
  notice(lines: string[], ms?: number): void;
  head(): THREE.Vector3;
  feet(): THREE.Vector3;
}

export class ModelShelf {
  readonly group = new THREE.Group();
  private readonly placed = new Map<string, Placed>();
  private readonly minis: { kind: ModelKind; mesh: THREE.Mesh }[] = [];
  /** Invisible boxes around each miniature: the organs alone are too thin to point at. */
  private readonly picks: THREE.InstancedMesh;
  private readonly specs = new Map<ModelKind, ModelSpec>();
  private readonly sign: CanvasPanel;
  private carry: Carry | undefined;
  private readonly wasSqueezing = new Map<string, boolean>();
  private hoveredByMouse: Placed | undefined;
  private lastStream = 0;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointerMatrix = new THREE.Matrix4();
  private readonly tmpMatrix = new THREE.Matrix4();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpScale = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly dummy = new THREE.Object3D();
  private readonly pinGeometry = new THREE.SphereGeometry(0.014, 10, 8);
  private readonly onWheel = (e: WheelEvent) => this.wheel(e);
  private readonly unsubscribe: (() => void)[] = [];
  /** Dev hook: the last thing that happened. */
  lastAction = '';

  constructor(
    private readonly view: StudyView,
    private readonly net: Net,
    private readonly interactive: InteractiveSystem,
    private readonly input: InputManager,
    private readonly handlers: ModelShelfHandlers,
  ) {
    this.group.name = 'model-shelf';
    const data = modelData(view.file);
    for (const kind of MODEL_KINDS) this.specs.set(kind, buildModel(kind, data));

    // The shelf unit: a low cabinet with a top, four miniatures and a sign, built round its
    // own origin and turned into place.
    const unit = new THREE.Group();
    unit.name = 'model-shelf-unit';
    unit.position.set(SHELF.x, 0, SHELF.z);
    unit.rotation.y = SHELF.yaw;
    this.group.add(unit);
    const cabinet = new THREE.Mesh(
      new THREE.BoxGeometry(SHELF.width, SHELF.y, 0.5),
      new THREE.MeshStandardMaterial({ color: '#6c645b', roughness: 0.6 }),
    );
    cabinet.position.set(0, SHELF.y / 2, 0);
    const top = new THREE.Mesh(
      new THREE.BoxGeometry(SHELF.width + 0.1, 0.04, 0.6),
      new THREE.MeshStandardMaterial({ color: '#c9b28c', roughness: 0.3, metalness: 0.85 }),
    );
    top.position.set(0, SHELF.y + 0.02, 0);
    unit.add(cabinet, top);
    const miniMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6 });
    MODEL_KINDS.forEach((kind, i) => {
      const spec = this.specs.get(kind)!;
      const mesh = new THREE.Mesh(mergedGeometry(spec), miniMaterial);
      const x = (i - (MODEL_KINDS.length - 1) / 2) * (SHELF.width / MODEL_KINDS.length);
      mesh.position.set(x, SHELF.y + 0.04, -0.05);
      mesh.scale.setScalar(SHELF.miniScale);
      mesh.name = `shelf-${kind}`;
      unit.add(mesh);
      this.minis.push({ kind, mesh });
    });
    this.picks = new THREE.InstancedMesh(
      new THREE.BoxGeometry(SHELF.width / MODEL_KINDS.length - 0.04, 0.34, 0.4),
      new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }),
      MODEL_KINDS.length,
    );
    this.picks.name = 'shelf-picks';
    this.minis.forEach((m, i) => {
      this.dummy.position.set(m.mesh.position.x, SHELF.y + 0.04 + 0.17, m.mesh.position.z);
      this.dummy.updateMatrix();
      this.picks.setMatrixAt(i, this.dummy.matrix);
    });
    this.picks.instanceMatrix.needsUpdate = true;
    this.picks.computeBoundingSphere();
    unit.add(this.picks);
    interactive.add({
      object: this.picks,
      onHover: (_p, point, hit) => {
        const kind = this.minis[hit?.instanceId ?? -1]?.kind;
        handlers.tooltip(
          kind
            ? [MODEL_TITLES[kind], MODEL_HINTS[kind], 'Select to place it on the table']
            : undefined,
          point,
        );
      },
      onLeave: () => handlers.tooltip(undefined, undefined),
      onSelect: (_p, _point, hit) => {
        const kind = this.minis[hit?.instanceId ?? -1]?.kind;
        if (kind) this.place(kind);
      },
    });
    // The sign above the shelf: what the shelf is for, then each model's name and use right
    // above its miniature.
    this.sign = new CanvasPanel({
      width: SHELF.width,
      height: 0.42,
      pixelWidth: 1280,
      pixelHeight: 336,
      name: 'model-shelf-sign',
      resolution: 1.5,
    });
    this.sign.mesh.position.set(0, 1.62, 0.2);
    this.sign.mesh.rotation.y = Math.PI;
    this.sign.setDraw((ctx, panel) => this.drawSign(ctx, panel));
    unit.add(this.sign.mesh);

    window.addEventListener('wheel', this.onWheel, { passive: true });
    this.unsubscribe.push(
      net.onModels((list) => this.setModels(list)),
      net.onModelPlaced((m) => this.add(m)),
      net.onModelUpdated((id, patch, from) => this.onUpdated(id, patch, from)),
      net.onModelPins((id, pins) => this.onPins(id, pins)),
      net.onModelRemoved((id) => this.remove(id)),
    );
    this.setModels(net.models);
  }

  get count(): number {
    return this.placed.size;
  }

  ids(): string[] {
    return [...this.placed.keys()];
  }

  state(id: string): SharedModel | undefined {
    return this.placed.get(id)?.model;
  }

  partNames(id: string): string[] {
    return this.placed.get(id)?.spec.parts.map((p) => p.name) ?? [];
  }

  get carrying(): string | undefined {
    return this.carry?.id;
  }

  private drawSign(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    fillRoundRect(ctx, { x: 0, y: 0, w: W, h: H }, 16, PALETTE.surface);
    text(ctx, 'MODEL SHELF', 24, 40, { size: 20, weight: 700, colour: PALETTE.teal });
    text(
      ctx,
      `Shared 3D props for the discussion at the table · ${this.placed.size} on the table · select one to place it, grip to carry, select a part to pin it`,
      24,
      70,
      { size: 12, colour: PALETTE.muted, maxWidth: W - 48 },
    );
    // Columns follow the miniatures; the sign faces the room, so the first model (lowest x)
    // is on the viewer's right.
    const colW = W / MODEL_KINDS.length;
    [...MODEL_KINDS].reverse().forEach((kind, j) => {
      const x = j * colW + 18;
      text(ctx, MODEL_TITLES[kind], x, 122, { size: 16, weight: 700, maxWidth: colW - 30 });
      ctx.font = font(13, 500);
      const lines = wrapText(ctx, MODEL_USES[kind], colW - 30);
      lines
        .slice(0, 7)
        .forEach((line, i) => text(ctx, line, x, 154 + i * 25, { size: 13, colour: PALETTE.text }));
    });
  }

  /* ---------- Placing ---------- */

  /** Places a model on the table on the viewer's side, at a free spot. */
  place(kind: ModelKind): void {
    const feet = this.handlers.feet();
    const spec = this.specs.get(kind)!;
    let angle = Math.atan2(feet.z - ROOM.tableCentre.z, feet.x - ROOM.tableCentre.x);
    const taken = [...this.placed.values()].map((p) => p.group.position);
    for (let i = 0; i < 8; i++) {
      const x = ROOM.tableCentre.x + Math.cos(angle) * PLACE_RADIUS;
      const z = ROOM.tableCentre.z + Math.sin(angle) * PLACE_RADIUS;
      const free = taken.every((t) => Math.hypot(t.x - x, t.z - z) > spec.radius + 0.25);
      if (free || i === 7) {
        const yaw = Math.atan2(feet.x - x, feet.z - z);
        const q = new THREE.Quaternion().setFromAxisAngle(this.up, yaw);
        this.net.modelPlace(kind, { x, y: TABLE_TOP, z }, [q.x, q.y, q.z, q.w], 1);
        this.lastAction = `place:${kind}`;
        return;
      }
      angle += (50 * Math.PI) / 180;
    }
  }

  /* ---------- Room state ---------- */

  private setModels(list: SharedModel[]): void {
    const keep = new Set(list.map((m) => m.id));
    for (const id of [...this.placed.keys()]) if (!keep.has(id)) this.remove(id);
    for (const m of list) this.add(m);
  }

  private add(model: SharedModel): void {
    if (this.placed.has(model.id)) {
      this.apply(this.placed.get(model.id)!, model);
      return;
    }
    const spec = this.specs.get(model.kind)!;
    const group = new THREE.Group();
    group.name = `model-${model.id}`;
    const parts = spec.parts.map((part) => {
      const mesh = new THREE.Mesh(
        part.geometry,
        new THREE.MeshStandardMaterial({
          color: part.colour,
          roughness: 0.55,
          metalness: 0.05,
          transparent: part.opacity !== undefined,
          opacity: part.opacity ?? 1,
          depthWrite: part.opacity === undefined,
        }),
      );
      mesh.name = part.name;
      mesh.position.set(...part.position);
      group.add(mesh);
      return mesh;
    });
    const ghost = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(spec.radius * 2, spec.height, spec.radius * 2)),
      new THREE.LineBasicMaterial({ color: 0x35b5b0, transparent: true, opacity: 0.9 }),
    );
    ghost.position.y = spec.height / 2;
    ghost.visible = false;
    group.add(ghost);
    const pick = new THREE.Mesh(
      new THREE.BoxGeometry(spec.radius * 2 + 0.08, spec.height + 0.08, spec.radius * 2 + 0.08),
      new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }),
    );
    pick.name = `model-pick-${model.id}`;
    pick.position.set(model.position.x, model.position.y + spec.height / 2, model.position.z);
    this.group.add(pick);
    const pins = new THREE.InstancedMesh(
      this.pinGeometry,
      new THREE.MeshBasicMaterial({ toneMapped: false }),
      24,
    );
    pins.count = 0;
    pins.frustumCulled = false;
    group.add(pins);
    const label = new CanvasPanel({
      width: 0.42,
      height: 0.2,
      pixelWidth: 420,
      pixelHeight: 200,
      name: `model-label-${model.id}`,
      resolution: 1.5,
    });
    label.mesh.position.y = spec.height + 0.14;
    label.mesh.renderOrder = 30;
    group.add(label.mesh);
    this.group.add(group);
    const placed: Placed = {
      model,
      spec,
      group,
      parts,
      label,
      ghost,
      pick,
      pins,
      hoveredPart: -1,
      interactive: {
        object: group,
        onHover: (_p, point, hit) => this.hoverPart(placed, hit?.object, point),
        onLeave: () => {
          placed.hoveredPart = -1;
          this.handlers.tooltip(undefined, undefined);
        },
        onSelect: (_p, _point, hit) => this.selectPart(placed, hit?.object),
      },
    };
    this.interactive.add(placed.interactive);
    label.setDraw((ctx, panel) => this.drawLabel(ctx, panel, placed));
    label.onSelect = (region) => this.selectLabel(placed, region);
    label.onHoverChange = (region, point) =>
      this.handlers.tooltip(region?.tooltip, region ? point : undefined);
    label.attach(this.interactive);
    this.placed.set(model.id, placed);
    this.apply(placed, model);
    this.sign.invalidate();
    if (model.placedBy.sessionId !== this.net.sessionId)
      this.handlers.notice(
        [
          `${model.placedBy.name} placed the ${MODEL_TITLES[model.kind].toLowerCase()}`,
          'On the table.',
        ],
        2500,
      );
  }

  private apply(placed: Placed, model: SharedModel): void {
    placed.model = model;
    placed.group.position.set(model.position.x, model.position.y, model.position.z);
    placed.group.quaternion.set(...model.quaternion);
    placed.group.scale.setScalar(model.scale);
    this.applyExplode(placed);
    this.applyOwner(placed);
    this.applyPins(placed);
    placed.label.invalidate();
  }

  private applyExplode(placed: Placed): void {
    placed.spec.parts.forEach((part, i) => {
      partPosition(part, placed.model.exploded, placed.parts[i]!.position);
    });
    this.applyPins(placed);
  }

  private applyOwner(placed: Placed): void {
    const owner = placed.model.owner;
    const mine = owner?.sessionId === this.net.sessionId;
    placed.ghost.visible = !!owner && !mine;
    for (const mesh of placed.parts) {
      const m = mesh.material as THREE.MeshStandardMaterial;
      m.emissive.set(owner && !mine ? 0x35b5b0 : 0x000000);
      m.emissiveIntensity = 0.25;
    }
  }

  private applyPins(placed: Placed): void {
    const colour = new THREE.Color();
    const list = placed.model.pins.slice(0, 24);
    list.forEach((pin, i) => {
      const index = placed.spec.parts.findIndex((p) => p.name === pin.part);
      const part = placed.spec.parts[index];
      if (!part) {
        this.dummy.position.set(0, -10, 0);
      } else {
        part.geometry.computeBoundingBox();
        const top = part.geometry.boundingBox?.max.y ?? 0;
        partPosition(part, placed.model.exploded, this.dummy.position);
        this.dummy.position.y += top + 0.025;
        // Several pins on one part fan out a little.
        const same = list.slice(0, i).filter((p) => p.part === pin.part).length;
        this.dummy.position.x += same * 0.03;
      }
      this.dummy.updateMatrix();
      placed.pins.setMatrixAt(i, this.dummy.matrix);
      placed.pins.setColorAt(i, colour.set(ROLE_STYLES[pin.by.role].hex));
    });
    placed.pins.count = list.length;
    placed.pins.instanceMatrix.needsUpdate = true;
    if (placed.pins.instanceColor) placed.pins.instanceColor.needsUpdate = true;
    placed.pins.computeBoundingSphere();
  }

  private onUpdated(id: string, patch: Partial<SharedModel>, from: string): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    Object.assign(placed.model, patch);
    // Our own carry keeps the local transform; everything else follows the room.
    if (from !== this.net.sessionId || this.carry?.id !== id) {
      if (patch.position)
        placed.group.position.set(patch.position.x, patch.position.y, patch.position.z);
      if (patch.quaternion) placed.group.quaternion.set(...patch.quaternion);
      if (patch.scale !== undefined) placed.group.scale.setScalar(patch.scale);
    }
    if (patch.exploded !== undefined) this.applyExplode(placed);
    if ('owner' in patch) {
      this.applyOwner(placed);
      if (patch.owner && patch.owner.sessionId !== this.net.sessionId && this.carry?.id === id) {
        // Somebody took it out of our hands.
        this.carry = undefined;
        this.handlers.notice(
          [`${patch.owner.name} took the ${MODEL_TITLES[placed.model.kind].toLowerCase()}`],
          2000,
        );
      }
    }
    placed.label.invalidate();
  }

  private onPins(id: string, pins: ModelPin[]): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    placed.model.pins = pins;
    this.applyPins(placed);
    placed.label.invalidate();
  }

  private remove(id: string): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    this.placed.delete(id);
    if (this.carry?.id === id) this.carry = undefined;
    this.interactive.remove(placed.interactive);
    placed.label.detach(this.interactive);
    placed.label.dispose();
    this.group.remove(placed.group);
    for (const mesh of placed.parts) (mesh.material as THREE.Material).dispose();
    this.group.remove(placed.pick);
    placed.pick.geometry.dispose();
    (placed.pick.material as THREE.Material).dispose();
    placed.ghost.geometry.dispose();
    (placed.ghost.material as THREE.Material).dispose();
    (placed.pins.material as THREE.Material).dispose();
    this.sign.invalidate();
  }

  /* ---------- Parts: hover, pin ---------- */

  private partOf(placed: Placed, object: THREE.Object3D | undefined): number {
    if (!object) return -1;
    if (object === placed.pins) return -2;
    return placed.parts.indexOf(object as THREE.Mesh);
  }

  private hoverPart(
    placed: Placed,
    object: THREE.Object3D | undefined,
    point: THREE.Vector3,
  ): void {
    const i = this.partOf(placed, object);
    if (i === placed.hoveredPart) return;
    placed.hoveredPart = i;
    const owner = placed.model.owner;
    const held =
      owner && owner.sessionId !== this.net.sessionId ? `Held by ${owner.name}` : undefined;
    if (i === -2) {
      this.handlers.tooltip(
        [
          'Pin',
          `${placed.model.pins.length} on this model`,
          'Select a pinned part again to remove your pin',
        ],
        point,
      );
      return;
    }
    const part = placed.spec.parts[i];
    if (!part) {
      this.handlers.tooltip(undefined, undefined);
      return;
    }
    const pins = placed.model.pins.filter((p) => p.part === part.name);
    this.handlers.tooltip(
      [
        `${part.name} · ${MODEL_TITLES[placed.model.kind]}`,
        part.detail ?? '',
        pins.length
          ? `Pinned by ${pins.map((p) => p.by.name).join(', ')}`
          : (held ?? 'Grip to carry · select to pin this part'),
      ].filter(Boolean),
      point,
    );
  }

  private selectPart(placed: Placed, object: THREE.Object3D | undefined): void {
    const i = this.partOf(placed, object);
    const part = placed.spec.parts[i];
    if (!part) return;
    this.togglePin(placed.model.id, part.name);
  }

  /** Pins a part, or removes the local user's pin on it. */
  togglePin(id: string, partName: string): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    const mine = placed.model.pins.find(
      (p) => p.part === partName && p.by.sessionId === this.net.sessionId,
    );
    if (mine) this.net.modelUnpin(id, mine.id);
    else this.net.modelPin(id, partName);
    this.lastAction = `${mine ? 'unpin' : 'pin'}:${partName}`;
  }

  setExploded(id: string, amount: number): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    const owner = placed.model.owner;
    if (owner && owner.sessionId !== this.net.sessionId) {
      this.handlers.notice([`${owner.name} is holding it`], 1500);
      return;
    }
    placed.model.exploded = amount;
    this.applyExplode(placed);
    placed.label.invalidate();
    this.net.modelUpdate(id, { exploded: amount });
    this.lastAction = `explode:${amount}`;
  }

  removeModel(id: string): void {
    this.net.modelRemove(id);
  }

  /* ---------- Label ---------- */

  private drawLabel(ctx: CanvasRenderingContext2D, panel: CanvasPanel, placed: Placed): void {
    const W = panel.pxW;
    const H = panel.pxH;
    const m = placed.model;
    fillRoundRect(ctx, { x: 0, y: 0, w: W, h: H }, 14, PALETTE.surface);
    text(ctx, MODEL_TITLES[m.kind], 16, 34, { size: 15, weight: 700 });
    const owner = m.owner;
    text(
      ctx,
      owner
        ? owner.sessionId === this.net.sessionId
          ? 'In your hands'
          : `Held by ${owner.name}`
        : `Placed by ${m.placedBy.name} · ${m.pins.length} pin${m.pins.length === 1 ? '' : 's'}`,
      16,
      58,
      { size: 11, colour: owner ? PALETTE.teal : PALETTE.muted, maxWidth: W - 32 },
    );
    const next = m.exploded >= 0.99 ? 0 : m.exploded >= 0.49 ? 1 : 0.5;
    const label =
      m.exploded >= 0.99 ? 'Assemble' : m.exploded >= 0.49 ? 'Explode fully' : 'Explode';
    let x = 16;
    const r1 = chip(ctx, label, x, 80, {
      active: m.exploded > 0,
      hovered: panel.isHovered('explode'),
      h: 32,
      size: 12,
    });
    panel.region({
      id: 'explode',
      ...r1,
      data: { exploded: next },
      tooltip: ['Explode into parts', `Now ${Math.round(m.exploded * 100)}%`],
    });
    x += r1.w + 8;
    const r2 = chip(ctx, 'Back to shelf', x, 80, {
      hovered: panel.isHovered('remove'),
      h: 32,
      size: 12,
      colour: PALETTE.crimson,
    });
    panel.region({
      id: 'remove',
      ...r2,
      data: { remove: true },
      tooltip: ['Removes it from the table for everyone'],
    });
    text(
      ctx,
      'Grip to carry · stick or , . to turn · wheel to scale · select a part to pin',
      16,
      140,
      {
        size: 10,
        colour: PALETTE.muted,
        maxWidth: W - 32,
      },
    );
    const pinned = m.pins.slice(-3).map((p) => `${p.part} (${p.by.name})`);
    if (pinned.length)
      text(ctx, pinned.join(' · '), 16, 166, { size: 10, colour: PALETTE.text, maxWidth: W - 32 });
  }

  private selectLabel(placed: Placed, region: Region): void {
    const data = region.data as { exploded?: number; remove?: boolean } | undefined;
    if (!data) return;
    if (data.exploded !== undefined) this.setExploded(placed.model.id, data.exploded);
    if (data.remove) this.removeModel(placed.model.id);
  }

  /* ---------- Per frame: carry, turn, labels ---------- */

  update(dt: number, now = performance.now()): void {
    this.hoveredByMouse = undefined;
    for (const p of this.input.activePointers()) {
      const squeezing = p.kind === 'mouse' ? this.input.keysDown.has('KeyG') : p.squeezing;
      const was = this.wasSqueezing.get(p.id) ?? false;
      this.wasSqueezing.set(p.id, squeezing);
      if (p.kind === 'mouse' && !squeezing) this.hoveredByMouse = this.hitTest(p);
      if (squeezing && !was) this.begin(p);
      else if (!squeezing && was && this.carry?.pointerId === p.id) this.end();
      if (this.carry && this.carry.pointerId === p.id && squeezing) {
        this.follow(p);
        if (p.kind !== 'mouse' && Math.abs(p.axes.x) > 0.3)
          this.turn(this.carry.id, -p.axes.x * dt * 2.2);
      }
    }
    const turning = this.carry ? this.placed.get(this.carry.id) : this.hoveredByMouse;
    if (turning) {
      if (this.input.keysDown.has('Comma')) this.turn(turning.model.id, dt * 2);
      if (this.input.keysDown.has('Period')) this.turn(turning.model.id, -dt * 2);
    }
    if (this.carry && now - this.lastStream >= STREAM_MS) {
      this.lastStream = now;
      this.stream(this.carry.id);
    }
    const head = this.handlers.head();
    for (const placed of this.placed.values()) {
      // The grip box follows the model (which may be carried, turned or scaled).
      const half = placed.spec.height / 2;
      placed.pick.position
        .set(0, half, 0)
        .applyQuaternion(placed.group.quaternion)
        .multiplyScalar(placed.group.scale.x)
        .add(placed.group.position);
      placed.pick.quaternion.copy(placed.group.quaternion);
      placed.pick.scale.setScalar(placed.group.scale.x);
      // Labels face the viewer.
      const p = placed.group.position;
      placed.label.mesh.rotation.y =
        Math.atan2(head.x - p.x, head.z - p.z) - this.yawOf(placed.group);
      placed.label.mesh.scale.setScalar(1 / placed.group.scale.x);
      placed.label.mesh.position.y = placed.spec.height + 0.14 + placed.model.exploded * 0.15;
      placed.label.update(now);
    }
    this.sign.update(now);
  }

  private yawOf(group: THREE.Group): number {
    const e = new THREE.Euler().setFromQuaternion(group.quaternion, 'YXZ');
    return e.y;
  }

  private hitTest(p: Pointer): Placed | undefined {
    this.raycaster.set(p.ray.origin, p.ray.direction);
    this.raycaster.far = 6;
    // Only the parts: the hidden ghost outline is a line, which three.js hits from a metre
    // away, and the label sits above; either one came first and the grip found no model.
    const objects = [...this.placed.values()].flatMap((x) => [x.pick, ...x.parts]);
    const hit = this.raycaster.intersectObjects(objects, false)[0];
    if (!hit) return undefined;
    for (const placed of this.placed.values())
      if (placed.pick === hit.object || placed.parts.includes(hit.object as THREE.Mesh))
        return placed;
    return undefined;
  }

  private frameOf(p: Pointer, target: THREE.Matrix4): THREE.Matrix4 {
    this.tmpPos.copy(p.ray.origin).add(p.ray.direction);
    target.lookAt(p.ray.origin, this.tmpPos, this.up);
    target.setPosition(p.ray.origin);
    return target;
  }

  private begin(p: Pointer): void {
    if (this.carry) return;
    const placed = this.hitTest(p);
    if (!placed) return;
    const owner = placed.model.owner;
    if (owner && owner.sessionId !== this.net.sessionId) {
      this.handlers.notice(
        [`${owner.name} is holding the ${MODEL_TITLES[placed.model.kind].toLowerCase()}`],
        1500,
      );
      return;
    }
    this.grab(placed.model.id, p);
  }

  /** Starts carrying a model with a pointer (dev hooks use the mouse pointer). */
  grab(id: string, p: Pointer): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    this.frameOf(p, this.pointerMatrix);
    placed.group.updateMatrixWorld(true);
    const offset = new THREE.Matrix4().multiplyMatrices(
      this.pointerMatrix.clone().invert(),
      placed.group.matrixWorld,
    );
    this.carry = { id, pointerId: p.id, offset };
    placed.model.owner = { sessionId: this.net.sessionId ?? 'me', name: '' };
    this.applyOwner(placed);
    this.net.modelGrab(id, true);
    this.lastAction = `grab:${placed.model.kind}`;
    placed.label.invalidate();
  }

  private follow(p: Pointer): void {
    const carry = this.carry;
    const placed = carry && this.placed.get(carry.id);
    if (!carry || !placed) return;
    this.frameOf(p, this.pointerMatrix);
    this.tmpMatrix.multiplyMatrices(this.pointerMatrix, carry.offset);
    this.tmpMatrix.decompose(this.tmpPos, this.tmpQuat, this.tmpScale);
    const parent = placed.group.parent;
    if (parent) {
      parent.worldToLocal(this.tmpPos);
      this.tmpQuat.premultiply(parent.getWorldQuaternion(new THREE.Quaternion()).invert());
    }
    placed.group.position.copy(this.tmpPos);
    // Models stay upright: keep the yaw only.
    const e = new THREE.Euler().setFromQuaternion(this.tmpQuat, 'YXZ');
    placed.group.quaternion.setFromEuler(new THREE.Euler(0, e.y, 0));
    // Never below the table top or the floor.
    placed.group.position.y = Math.max(0.02, placed.group.position.y);
  }

  private turn(id: string, radians: number): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    const owner = placed.model.owner;
    if (owner && owner.sessionId !== this.net.sessionId) return;
    placed.group.rotateY(radians);
    if (!this.carry || this.carry.id !== id) this.stream(id);
  }

  end(): void {
    const carry = this.carry;
    if (!carry) return;
    this.carry = undefined;
    const placed = this.placed.get(carry.id);
    if (!placed) return;
    this.stream(carry.id);
    this.net.modelGrab(carry.id, false);
    placed.model.owner = null;
    this.applyOwner(placed);
    placed.label.invalidate();
    this.lastAction = `release:${placed.model.kind}`;
  }

  private stream(id: string): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    const p = placed.group.position;
    const q = placed.group.quaternion;
    placed.model.position = { x: r(p.x), y: r(p.y), z: r(p.z) };
    placed.model.quaternion = [r(q.x, 4), r(q.y, 4), r(q.z, 4), r(q.w, 4)];
    placed.model.scale = r(placed.group.scale.x);
    this.net.modelUpdate(id, {
      position: placed.model.position,
      quaternion: placed.model.quaternion,
      scale: placed.model.scale,
    });
  }

  private wheel(e: WheelEvent): void {
    const target = this.carry ? this.placed.get(this.carry.id) : this.hoveredByMouse;
    if (!target) return;
    const owner = target.model.owner;
    if (owner && owner.sessionId !== this.net.sessionId) return;
    const scale = THREE.MathUtils.clamp(
      target.group.scale.x * (e.deltaY < 0 ? 1.1 : 1 / 1.1),
      MIN_SCALE,
      MAX_SCALE,
    );
    target.group.scale.setScalar(scale);
    this.stream(target.model.id);
    this.lastAction = `scale:${scale.toFixed(2)}`;
  }

  /** Dev hook: moves a model as a carry would. */
  moveTo(id: string, x: number, y: number, z: number): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    placed.group.position.set(x, y, z);
    this.stream(id);
  }

  /** Dev hook: scales a model without the wheel. */
  setScale(id: string, scale: number): void {
    const placed = this.placed.get(id);
    if (!placed) return;
    placed.group.scale.setScalar(THREE.MathUtils.clamp(scale, MIN_SCALE, MAX_SCALE));
    this.stream(id);
  }

  dispose(): void {
    window.removeEventListener('wheel', this.onWheel);
    for (const u of this.unsubscribe) u();
    for (const id of [...this.placed.keys()]) this.remove(id);
    this.sign.detach(this.interactive);
    this.sign.dispose();
    this.picks.geometry.dispose();
    (this.picks.material as THREE.Material).dispose();
    for (const spec of this.specs.values()) for (const p of spec.parts) p.geometry.dispose();
    this.pinGeometry.dispose();
  }
}

function r(v: number, decimals = 3): number {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}
